import * as Sentry from '@sentry/react';

const env = (typeof import.meta !== 'undefined' && (import.meta as any)?.env) || (typeof process !== 'undefined' && process.env) || {};
const SENTRY_DSN = env.VITE_SENTRY_DSN || env.SENTRY_DSN;
const ENVIRONMENT = env.VITE_SENTRY_ENVIRONMENT || env.MODE || env.NODE_ENV || 'development';
const RELEASE = 'www-myservicehub-africa@1.0.0';

/**
 * Robust filter to aggressively sanitize sensitive data
 * Prevents leaks of passwords, WhatsApp numbers, customer messages, auth tokens, emails, or identity docs.
 */
export function sanitizeSensitiveData(data: any, seen: WeakSet<object> = new WeakSet()): any {
  if (data === null || data === undefined) return data;

  if (typeof data === 'string') {
    // Mask typical email patterns
    const emailRegex = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g;
    // Mask typical phone/whatsapp number patterns (more than 7 digits)
    const phoneRegex = /\+?[0-9]{7,15}/g;
    
    let sanitized = data;
    sanitized = sanitized.replace(emailRegex, '[EMAIL_REDACTED]');
    sanitized = sanitized.replace(phoneRegex, '[PHONE_REDACTED]');
    return sanitized;
  }

  if (typeof data !== 'object') {
    return data;
  }

  // Prevent circular reference crashes
  if (seen.has(data)) {
    return '[CIRCULAR_REF]';
  }
  seen.add(data);

  if (Array.isArray(data)) {
    return data.map(item => sanitizeSensitiveData(item, seen));
  }

  // Handle Error instances safely
  if (data instanceof Error) {
    return {
      name: data.name,
      message: sanitizeSensitiveData(data.message, seen),
      stack: sanitizeSensitiveData(data.stack, seen)
    };
  }

  try {
    const sanitizedObj: Record<string, any> = {};
    const sensitiveKeys = [
      'password', 'pass', 'token', 'auth', 'whatsapp', 'number', 'phone', 
      'message', 'msg', 'email', 'document', 'doc_url', 'secret', 'key',
      'cvv', 'card', 'authorization', 'credential', 'private', 'customer_name',
      'customer_whatsapp'
    ];

    for (const key of Object.keys(data)) {
      if (!key) continue;
      const lowerKey = String(key).toLowerCase();
      const isSensitive = sensitiveKeys.some(sk => lowerKey.includes(sk));

      if (isSensitive) {
        sanitizedObj[key] = '[REDACTED_SENSITIVE]';
      } else {
        sanitizedObj[key] = sanitizeSensitiveData(data[key], seen);
      }
    }
    return sanitizedObj;
  } catch {
    return String(data);
  }
}

/**
 * Initialize Sentry globally for React Frontend with robust tracing and error monitoring
 */
export function initSentry() {
  if (!SENTRY_DSN) {
    console.info('[Sentry] Client-side DSN not set in VITE_SENTRY_DSN. Running in local mode.');
    return;
  }

  try {
    const integrations: any[] = [];
    if (typeof Sentry.browserTracingIntegration === 'function') {
      try {
        integrations.push(Sentry.browserTracingIntegration());
      } catch (intErr) {
        console.warn('[Sentry] Browser tracing integration failed to initialize:', intErr);
      }
    }

    Sentry.init({
      dsn: SENTRY_DSN,
      environment: ENVIRONMENT,
      release: RELEASE,
      integrations,
      tracesSampleRate: ENVIRONMENT === 'production' ? 0.1 : 1.0,

      beforeSend(event) {
        if (!event) return event;
        try {
          if (event.extra && typeof event.extra === 'object') {
            event.extra = sanitizeSensitiveData(event.extra);
          }
          if (event.request && typeof event.request === 'object') {
            if (event.request.headers && typeof event.request.headers === 'object') {
              const sensitive = ['authorization', 'cookie', 'x-api-key', 'token', 'session'];
              for (const h of Object.keys(event.request.headers)) {
                if (sensitive.some(key => h.toLowerCase().includes(key))) {
                  event.request.headers[h] = '[REDACTED]';
                }
              }
            }
          }
        } catch {
          // In case sanitization encounters unexpected structure, do not crash Sentry pipeline
        }
        return event;
      }
    });

    console.log(`[Sentry] Client-side initialized successfully. Env: ${ENVIRONMENT}`);
  } catch (err) {
    console.info('[Sentry] Initialization skipped due to environment constraints:', err);
  }
}

/**
 * Sentry capture wrapper that forces robust sanitization and tags errors safely
 */
export function captureFrontendException(error: any, category: string, extraTags?: Record<string, any>, extraContext?: Record<string, any>) {
  if (!error) return;

  try {
    const sanitizedContext = sanitizeSensitiveData(extraContext || {});
    const errObj = error instanceof Error ? error : new Error(typeof error === 'string' ? error : JSON.stringify(error));
    
    Sentry.withScope((scope) => {
      scope.setTag('category', category || 'general');
      scope.setTag('flow_type', extraTags?.flow_type || 'general');
      
      if (extraTags && typeof extraTags === 'object') {
        Object.entries(extraTags).forEach(([key, val]) => {
          if (val !== undefined && val !== null) {
            scope.setTag(key, String(val));
          }
        });
      }

      if (sanitizedContext && typeof sanitizedContext === 'object') {
        scope.setExtra('context', sanitizedContext);
      }

      Sentry.captureException(errObj);
    });
  } catch (sentryErr) {
    console.warn('[Sentry] Failed to capture frontend exception:', sentryErr);
  }
}

